import React, { useState, useEffect } from 'react';
import { MDBContainer, MDBCardBody, MDBTable, MDBTableHead, MDBTableBody } from 'mdb-react-ui-kit';
import { Button, Modal, Toast } from 'react-bootstrap';
import SideDrawer from '../components/SideDrawer';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { getShortlists, deleteShortlist } from '../services/api';
import ShortlistRow from '../components/ShortlistRow';
import ShortlistDetailsDrawer from '../components/ShortlistDetailsDrawer';

export default function Shortlist() {
    const [searchTerm, setSearchTerm] = useState('');
    const { isCollapsed } = useAuth();
    const [shortlists, setShortlists] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [shortlistToDelete, setShortlistToDelete] = useState(null);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastType, setToastType] = useState('success');
    const [selectedShortlist, setSelectedShortlist] = useState(null);
    const [showDetailsDrawer, setShowDetailsDrawer] = useState(false);

    useEffect(() => {
        const fetchShortlists = async () => {
            try {
                const { data } = await getShortlists();
                setShortlists(data);
            } catch (error) {
                console.error('Error fetching shortlist data:', error);
            }
        };

        fetchShortlists();
    }, []);

    const showToastMessage = (message, type = 'success') => {
        setToastMessage(message);
        setToastType(type);
        setShowToast(true);
    };

    const handleDeleteClick = (shortlist) => {
        setShortlistToDelete(shortlist);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!shortlistToDelete) return;

        try {
            await deleteShortlist(shortlistToDelete.id);
            setShortlists((prevShortlists) => prevShortlists.filter(item => item.id !== shortlistToDelete.id));
            setShowDeleteModal(false);
            setShortlistToDelete(null);
            showToastMessage('Shortlist deleted successfully!', 'success');
        } catch (error) {
            console.error('Error deleting shortlist:', error);
            showToastMessage('Failed to delete shortlist.', 'error');
        }
    };

    const handleRowClick = (shortlist) => {
        setSelectedShortlist(shortlist);
        setShowDetailsDrawer(true);
    };

    // Export to CSV function
    const handleExportCSV = () => {
        if (!shortlists.length) {
            alert("No shortlists to export.");
            return;
        }

        // Define CSV headers (adjust as necessary)
        const headers = ["ID", "Name", "Creator", "CreatedAt"];
        const csvRows = [];
        csvRows.push(headers.join(","));

        // Map over shortlists and create rows
        shortlists.forEach(item => {
            const row = [
                item.id,
                `"${item.name}"`,
                `"${item.username}"`, // Assuming "username" holds the creator information
                `"${item.createdAt ? new Date(item.createdAt).toLocaleString() : ''}"`
            ];
            csvRows.push(row.join(","));
        });

        const csvString = csvRows.join("\n");
        const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", "shortlists.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <MDBContainer fluid className="App">
            <SideDrawer />
            <div className="main-container d-flex flex-column" style={{ marginLeft: isCollapsed ? '6%' : '13.6%' }}>
                <div className="col">
                    <Header />
                    <div className='mx-4 my-3 activity-log-container'>
                        <MDBCardBody>
                            <div className="d-flex align-items-center mb-2">
                                <input
                                    type="text"
                                    placeholder="Search by Name or Created By..."
                                    className="form-control me-2"
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <Button variant="secondary" onClick={handleExportCSV}>
                                    Export
                                </Button>
                            </div>


                            <div className="table-responsive my-3">
                                <MDBTable align='left' striped hover>
                                    <MDBTableHead>
                                        <tr style={{ backgroundColor: '#f0f0f0', color: '#7d7d7d' }}>
                                            <th>Name</th>
                                            <th>Creator Count</th>
                                            <th>Created By</th>
                                            <th>Action</th>
                                        </tr>
                                    </MDBTableHead>
                                    <MDBTableBody>
                                        {shortlists
                                            .filter((item) =>
                                                item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                                item.username.toLowerCase().includes(searchTerm.toLowerCase()))
                                            .map((item) => (
                                                <ShortlistRow
                                                    key={item.id}
                                                    shortlist={item}
                                                    onRowClick={handleRowClick}
                                                    onDeleteClick={handleDeleteClick}
                                                />
                                            ))}
                                    </MDBTableBody>
                                </MDBTable>
                            </div>
                        </MDBCardBody>
                    </div>
                </div>
            </div>

            <ShortlistDetailsDrawer
                show={showDetailsDrawer}
                onClose={() => setShowDetailsDrawer(false)}
                shortlist={selectedShortlist}
            />

            <Toast show={showToast} onClose={() => setShowToast(false)} delay={3000} autohide
                style={{ position: 'fixed', top: 20, right: 20, zIndex: 2000, backgroundColor: toastType === 'success' ? 'green' : 'red', color: 'white', padding: '10px 20px', borderRadius: '5px' }}>
                <Toast.Body>{toastMessage}</Toast.Body>
            </Toast>

            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete <strong>{shortlistToDelete?.name}</strong>?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDelete}>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>
        </MDBContainer>
    );
}
